import React from 'react';

// The one icon set for the Settings surface.
// Cool-crisp inline SVG glyphs — no emoji, no innerHTML, no icon dependency.
// 16px box, 1.4 stroke, round caps — one visual language across all tabs.

interface IconProps {
  name: string;
  size?: number;
  style?: React.CSSProperties;
}

export function Icon({ name, size = 16, style }: IconProps) {
  const strokeWidth = Math.round((1.4 * 16 / size) * 100) / 100;
  const p = {
    width: size, height: size, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor',
    strokeWidth, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true, style,
  };
  switch (name) {
    case 'userplus': return (<svg {...p}><circle cx="6" cy="5.4" r="2.5" /><path d="M1.8 13.4c0-2.3 1.9-3.6 4.2-3.6" /><path d="M11.6 8.4v4M9.6 10.4h4" /></svg>);
    case 'users': return (<svg {...p}><circle cx="5.6" cy="6" r="2.2" /><path d="M1.4 13c0-2 1.9-3.2 4.2-3.2s4.2 1.2 4.2 3.2" /><path d="M10.6 4.3a2.2 2.2 0 010 3.9M11.2 13c0-1.7-.8-2.7-2-3.2 2.1 0 3.9 1.2 3.9 3.2" /></svg>);
    case 'layers': return (<svg {...p}><path d="M8 2l5.6 3L8 8 2.4 5 8 2z" /><path d="M2.4 8L8 11l5.6-3M2.4 11L8 14l5.6-3" /></svg>);
    case 'send': return (<svg {...p}><path d="M14 2L7 9M14 2l-4.4 12-2.6-5L2 6.4 14 2z" /></svg>);
    case 'plus': return (<svg {...p}><path d="M8 3.4v9.2M3.4 8h9.2" /></svg>);
    case 'shield': return (<svg {...p}><path d="M8 1.9l5 1.9v3.9c0 3-2 5.2-5 6.4-3-1.2-5-3.4-5-6.4V3.8l5-1.9z" /><circle cx="8" cy="7" r="1.2" /><path d="M8 8.2v2" /></svg>);
    case 'bucket': return (<svg {...p}><ellipse cx="8" cy="3.7" rx="5" ry="1.8" /><path d="M3 3.7v8.6c0 1 2.2 1.8 5 1.8s5-.8 5-1.8V3.7" /><path d="M3 8c0 1 2.2 1.8 5 1.8s5-.8 5-1.8" /></svg>);
    case 'board': return (<svg {...p}><rect x="2.2" y="2.6" width="11.6" height="10.8" rx="1.4" /><path d="M5.7 5.7v5.1M10.3 5.7v3" /></svg>);
    case 'chat': return (<svg {...p}><path d="M13.4 8.1c0 2.5-2.4 4.5-5.4 4.5-.7 0-1.4-.1-2-.3l-3 1.2.9-2.4c-1-.8-1.3-1.8-1.3-3C2.6 5.6 5 3.6 8 3.6s5.4 2 5.4 4.5z" /><path d="M6 7.5h4M6 9.5h2.6" /></svg>);
    case 'mail': return (<svg {...p}><rect x="2" y="3.4" width="12" height="9.2" rx="1.3" /><path d="M2.5 4.5L8 8.6l5.5-4.1" /></svg>);
    case 'book': return (<svg {...p}><path d="M2.6 3.4h3.6c1 0 1.8.8 1.8 1.8v7.4c0-.8-.7-1.4-1.5-1.4H2.6z" /><path d="M13.4 3.4H9.8c-1 0-1.8.8-1.8 1.8v7.4c0-.8.7-1.4 1.5-1.4h3.5z" /></svg>);
    case 'ticket': return (<svg {...p}><path d="M2.4 6.2a1.4 1.4 0 000 3.6v2c0 .7.6 1.3 1.3 1.3h8.6c.7 0 1.3-.6 1.3-1.3v-2a1.4 1.4 0 000-3.6v-2c0-.7-.6-1.3-1.3-1.3H3.7c-.7 0-1.3.6-1.3 1.3v2z" /><path d="M6.4 3.6v8.8" strokeDasharray="1.6 1.6" /></svg>);
    case 'devices': return (<svg {...p}><rect x="1.8" y="2.6" width="9" height="6.6" rx="1" /><path d="M4.2 12.2h5" /><rect x="10.4" y="6.4" width="3.8" height="6.4" rx="0.9" /></svg>);
    case 'plug': return (<svg {...p}><path d="M5.4 1.8v3.4M10.6 1.8v3.4" /><path d="M3.4 5.2h9.2v2.4a4.6 4.6 0 01-9.2 0z" /><path d="M8 9.8v4.4" /></svg>);
    case 'history': return (<svg {...p}><circle cx="8" cy="8.4" r="5.8" /><path d="M8 5.2v3.2l2.4 1.4" /><path d="M2.6 3.4v3h3" /></svg>);
    case 'trash': return (<svg {...p}><path d="M2.6 4.4h10.8" /><path d="M6 4.4V3a1 1 0 011-1h2a1 1 0 011 1v1.4" /><path d="M4 4.4l.6 8.4a1.3 1.3 0 001.3 1.2h4.2a1.3 1.3 0 001.3-1.2l.6-8.4" /><path d="M6.6 7.4v4M9.4 7.4v4" /></svg>);
    case 'database': return (<svg {...p}><ellipse cx="8" cy="3.8" rx="5.2" ry="1.8" /><path d="M2.8 3.8v8.4c0 1 2.3 1.8 5.2 1.8s5.2-.8 5.2-1.8V3.8" /><path d="M2.8 8c0 1 2.3 1.8 5.2 1.8s5.2-.8 5.2-1.8" /></svg>);
    default: return null;
  }
}

interface TitleIconProps {
  name: string;
}

export function TitleIcon({ name }: TitleIconProps) {
  return (
    <span aria-hidden="true" className="stg-titleicon"><Icon name={name} size={15} /></span>
  );
}

export default Icon;
